// React / Router / Redux
import { forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

// UI libraries
import { Button, Space, Image } from 'antd';

// Common components
import {
  dialogFunction,
  RibbonButton,
  RibbonDivider,
  StyledModal,
  WithLoading,
} from 'common/components';
import selectorsGridView from 'common/components/grid-view/controllers/selectors';

// Assets
import ixonecaptureLogo from 'assets/product/product-upload-wizard/ixone-capture.png';

// Utils
import { toArray } from 'utils/array';

// Hooks (shared)
import { useGetProductFullView, useGridView, useVisibleModal } from 'hooks';
import useCheckIXOneCapturePermission from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useCheckIXOneCapturePermission';
import { useBrokerHasInitiateBillingEvent } from 'hooks/impersonate/useBrokerHasInitiateBillingEvent';
import { useCheckPaymentTermMonthAndMemberStatusCreditReview } from 'hooks/member/useCheckPaymentTermMonthAndMemberStatusCreditReview';

// Wizard context & hooks
import WizardProvider from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/WizardContext';
import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';
import usePendingWizardForm from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/usePendingWizardForm';
import useOnlineGrid from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useOnlineGrid';
import { useWizardSubmission } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useWizardSubmission';

// Wizard components
import { ProductUploadWizard } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/ProductUploadWizard';
import OnlineGridSection, {
  WizardGrid,
  WizardGridAction,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/OnlineGridModal';
import PendingFormModal from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardPendingForm';
import { WizardModalGroup } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardModalGroup';
import RecaptureModal from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/RecaptureModal';

// Wizard configs / constants
import { MODAL_TYPES } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepModalConstants';
import { onlineGridModalProps } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/constants/wizardModalProps';

// Wizard utils

// Styles
import 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/styles/Wizard.less';
import './IXOneSection.less';

const IXOneSectionContent = forwardRef(
  ({ width, isDetailView = false, ...otherProps }, ref) => {
  const { id } = useParams();

  const [visibleModal, { onModal, offModal }] = useVisibleModal();

  const { openModal, state, actions, isModalOpen, closeModal } = useWizard();

  const { hasPermissionIXOneCapture } = useCheckIXOneCapturePermission();
  const isPaymentTermMonthAndMemberStatusCreditReview =
    useCheckPaymentTermMonthAndMemberStatusCreditReview();
  const brokerHasInitiateBillingEvent = useBrokerHasInitiateBillingEvent();

  const { detailItems } = useGridView();

  const { productFull } = useGetProductFullView({
    productId: parseInt(id),
    enabled: isDetailView,
  });

  const isOwnerProduct = isDetailView
    ? productFull?.isOwner
    : detailItems?.every((item) => item?.isOwner);

  const hiddenByPermission =
    !hasPermissionIXOneCapture ||
    isPaymentTermMonthAndMemberStatusCreditReview ||
    !brokerHasInitiateBillingEvent;

  const hidden = hiddenByPermission || !isOwnerProduct;

  const {
    gridRef,
    selectedRow,
    productModal,
    isSaving,
    isValidating,
    memberId,
    recaptureModalVisible,
    recaptureTargetRow,
    handleAddRecaptureProducts,
    handleCloseRecaptureModal,
    unitUpcOptions,
    onSelectionChange,
    handleEditProduct,
    handleDeleteProduct,
    handleSubmitProduct,
    handleCloseProductModal,
    handleValidate,
    handleSave,
    handleCellValueChanged,
    handleRecaptureRow,
    handleAddNewProduct,
    handleAddRecapture,
    handleClearSelectedRow,
  } = useOnlineGrid(state, actions);

  const {
    isCanceling,
    wizardFormId,
    showWizardContent,
    checkPendingForm,
    handleContinue: handleContinuePendingForm,
    handleCancelForm,
    handleClose: handleClosePendingForm,
    cancelPendingFormAsync,
  } = usePendingWizardForm({
    enabled: false,
    actions,
    onValidate: handleValidate,
    memberId,
    onClose: offModal,
  });

  const {
    handleSaveAndPrepareSubmit,
    handleSubmissionContinue,
    hasErrors,
    isSubmitting,
    passedProductCount,
    passedProduct,
    wizardPaymentInfo,
  } = useWizardSubmission({
    state,
    actions,
    openModal,
    closeModal,
    memberId,
    handleValidate,
    onZeroCostSuccess: () => {
      closeModal(MODAL_TYPES.DIRECT_ONLINE_GRID);
      closeModal(MODAL_TYPES.CONFIRM_SUBMISSION);
    },
    gridRef,
  });

  const handleCloseDirectGridModal = () => {
    closeModal(MODAL_TYPES.DIRECT_ONLINE_GRID);
    actions.reset();
  };

  const handleSubmitPassedProducts = async () => {
    // TODO: Implement submit passed products only
  };

  const handleCloseSubmissionResult = () => {
    closeModal(MODAL_TYPES.ACCEPT_SUBMISSION);
    handleCloseDirectGridModal();
  };

  const handleOpenWizard = async () => {
    // Check for pending form first - returns boolean
    const hasPendingForm = await checkPendingForm();

    if (hasPendingForm) {
      // Show pending form modal - user will choose to continue, start new, or cancel
      openModal(MODAL_TYPES.PENDING_FORM);
      return;
    }

    // No pending form - show the dialog to ask if they want to add more products
    onModal();
  };

  // Handler when user chooses to continue pending form
  const handleContinuePendingFormAndOpenWizard = async () => {
    await handleContinuePendingForm();
    closeModal(MODAL_TYPES.PENDING_FORM);
    openModal(MODAL_TYPES.DIRECT_ONLINE_GRID);
  };

  // Handler when user chooses to start new form (cancels pending)
  const handleNewFormAndShowDialog = async () => {
    // First confirm with user
    dialogFunction({
      type: 'warn',
      content:
        'Starting a new form will delete your previous submission. Are you sure you want to continue?',
      okText: 'Continue',
      cancelText: 'Cancel',
      width: 500,
      onOk: async () => {
        const response = await cancelPendingFormAsync();

        if (response?.isSuccess) {
          closeModal(MODAL_TYPES.PENDING_FORM);
          // After canceling pending form, show the dialog
          onModal();
        }
      },
    });
  };

  // Handler when user closes pending form modal
  const handleClosePendingFormModal = () => {
    handleClosePendingForm();
    closeModal(MODAL_TYPES.PENDING_FORM);
  };

  useImperativeHandle(ref, () => ({
    openWizard: handleOpenWizard,
  }));

  return (
    <>
      {!hidden && (
        <>
          <RibbonButton
            icon={
              <Image
                className='ixone-section__image'
                src={ixonecaptureLogo}
                preview={false}
              />
            }
            label='IX-ONE Capture'
            className='ixone-section__btn'
            onClick={handleOpenWizard}
            {...otherProps}
          />
          <RibbonDivider />
        </>
      )}

      {/* Full Wizard Modal */}
      {visibleModal && (
        <ProductUploadWizard
          visible={visibleModal}
          onClose={offModal}
          wizardFormId={wizardFormId}
          showWizardContent={showWizardContent}
        />
      )}
      {/* Pending Form Modal */}
      {isModalOpen(MODAL_TYPES.PENDING_FORM) && (
        <PendingFormModal
          visible
          isCanceling={isCanceling}
          isValidating={isValidating}
          onClose={handleClosePendingFormModal}
          onCancelForm={handleCancelForm}
          onContinue={handleContinuePendingFormAndOpenWizard}
          onNewForm={handleNewFormAndShowDialog}
        />
      )}
      {/* Direct Online Grid Modal */}
      {isModalOpen(MODAL_TYPES.DIRECT_ONLINE_GRID) && (
        <StyledModal
          {...onlineGridModalProps}
          zIndex={1010}
          visible
          onCancel={handleCloseDirectGridModal}
          footer={
            <Space>
              <Button onClick={handleCloseDirectGridModal}>Cancel</Button>
              <Button
                type='primary'
                onClick={handleSave}
                loading={isSaving}
                disabled={isValidating || state.isGridLoading}
              >
                Save
              </Button>
              <Button
                type='primary'
                loading={isSubmitting || state.isGridLoading}
                disabled={isValidating || isSaving || state.isGridLoading}
                onClick={async () => {
                  await handleSaveAndPrepareSubmit({
                    wizardFormId: state.wizardFormId,
                  });
                }}
              >
                Submit
              </Button>
            </Space>
          }
        >
          <OnlineGridSection
            onAddNewProduct={handleAddNewProduct}
            onFindProducts={handleAddRecapture}
            disabled={isValidating || isSaving || state.isGridLoading}
            wizardGridAction={
              <WizardGridAction
                onAddNewProduct={handleAddNewProduct}
                onFindProducts={handleAddRecapture}
                onEditProduct={handleEditProduct}
                onDeleteProduct={handleDeleteProduct}
                selectedRow={selectedRow}
                disabled={isValidating || isSaving || state.isGridLoading}
              />
            }
            wizardGrid={
              <WithLoading
                loading={state.isGridLoading || isValidating || isSaving}
                tip='Processing...'
              >
                <WizardGrid
                  gridRef={gridRef}
                  onSelectionChange={onSelectionChange}
                  onCellValueChanged={handleCellValueChanged}
                  onRecaptureRow={handleRecaptureRow}
                  memberId={memberId}
                  unitUpcOptions={unitUpcOptions}
                  onClearSelectedRow={handleClearSelectedRow}
                />
              </WithLoading>
            }
          />
        </StyledModal>
      )}
      <WizardModalGroup
        productModal={productModal}
        isSubmitting={isSubmitting}
        wizardFormId={wizardFormId}
        wizardPaymentInfo={wizardPaymentInfo}
        hasErrors={hasErrors}
        passedProduct={passedProduct}
        passedProductCount={passedProductCount}
        onCloseProductModal={handleCloseProductModal}
        onCloseWizard={offModal}
        onSubmitProduct={handleSubmitProduct}
        onSubmissionContinue={handleSubmissionContinue}
        onSubmitPassedProducts={handleSubmitPassedProducts}
        onReviewProducts={() => {
          closeModal(MODAL_TYPES.ACCEPT_SUBMISSION);
        }}
        onAcceptCancel={handleCloseSubmissionResult}
      />

      {recaptureModalVisible && (
        <RecaptureModal
          visible={recaptureModalVisible}
          onCancel={handleCloseRecaptureModal}
          onAddProducts={handleAddRecaptureProducts}
          existingIds={state.products
            .map((p) => p.productUnitId)
            .filter(Boolean)}
          isSingleSelect={!!recaptureTargetRow}
        />
      )}
    </>
  );
  }
);

const IXOneSection = forwardRef((props, ref) => {
  const { id: productId } = useParams();
  const { isDetailView = false } = props;

  const selectProductGrid =
    useSelector(selectorsGridView.makeSelectDetailCurrentITemsSelection()) ??
    [];

  const selectedProductIdsGrid =
    selectProductGrid?.map((product) => parseInt(product?.id)) ?? [];

  const initialProductIds = isDetailView
    ? toArray(parseInt(productId))
    : selectedProductIdsGrid;

  return (
    <>
      <WizardProvider productIds={initialProductIds}>
        <IXOneSectionContent {...props} ref={ref} />
      </WizardProvider>
    </>
  );
});

export default IXOneSection;
